import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Chip,
  Divider,
  Button
} from '@mui/material';
import {
  CheckCircle,
  Schedule as ScheduleIcon,
  LocationOn,
  Person,
  Phone,
  Navigation
} from '@mui/icons-material';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import LoadingState from '../shared/LoadingState';

const WorkerSchedule = () => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  // Read-only week preview — running the day (En Route / outcomes)
  // happens on Home, where outcome enforcement lives. Actions here
  // bypassed that gate and silently swallowed its errors.

  useEffect(() => {
    const loadJobs = async () => {
      try {
        const response = await api.get('/admin/my-jobs');
        const allJobs = response.data || [];
        const today = new Date().toLocaleDateString('en-CA');
        const upcoming = allJobs
          .filter(j => (j.scheduled_date ? j.scheduled_date.split('T')[0] : '9999') >= today)
          .sort((a, b) => {
            const dateCompare = ((a.scheduled_date || '9999').split('T')[0]).localeCompare((b.scheduled_date || '9999').split('T')[0]);
            if (dateCompare !== 0) return dateCompare;
            return (a.scheduled_time || '99:99').localeCompare(b.scheduled_time || '99:99');
          });
        setJobs(upcoming);
      } catch (err) {
        console.error('Failed to load jobs:', err);
      }
      setLoading(false);
    };
    loadJobs();
  }, []);

  if (loading) {
    return <LoadingState message="Loading your schedule..." />;
  }

  const grouped = {};
  jobs.forEach(job => {
    const date = job.scheduled_date ? job.scheduled_date.split('T')[0] : 'Unscheduled';
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(job);
  });

  const sortedDates = Object.keys(grouped).sort();

  const formatDateLabel = (dateStr) => {
    if (dateStr === 'Unscheduled') return 'Unscheduled';
    const [year, month, day] = dateStr.split('-');
    const d = new Date(Number(year), Number(month) - 1, Number(day));
    if (isNaN(d.getTime())) return 'Invalid Date';
    return d.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'short' });
  };

  const todayStr = new Date().toLocaleDateString('en-CA');

  return (
    <Box sx={{ pb: 10 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={800} color="text.primary">My Schedule</Typography>
        <Typography variant="body2" color="text.secondary">Upcoming jobs</Typography>
      </Box>

      {sortedDates.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            bgcolor: '#F8FAFC',
            border: '1px dashed #E2E8F0'
          }}
        >
          <CheckCircle color="success" sx={{ fontSize: 64, mb: 2, opacity: 0.8 }} />
          <Typography variant="h6" fontWeight={600} gutterBottom>No upcoming jobs</Typography>
          <Typography variant="body2" color="text.secondary">You're all caught up!</Typography>
        </Paper>
      ) : (
        sortedDates.map(date => (
          <Box key={date} sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <ScheduleIcon color={date === todayStr ? 'primary' : 'action'} fontSize="small" />
              <Typography
                variant="subtitle1"
                fontWeight={700}
                color={date === todayStr ? 'primary.main' : 'text.primary'}
              >
                {formatDateLabel(date)}
              </Typography>
              {date === todayStr && (
                <Chip label="TODAY" size="small" color="primary" sx={{ fontWeight: 700 }} />
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />
            {grouped[date].map((job, idx) => {
              // Jobs imported before the name fix carry the address as
              // client_name/description — detect and stop the triple echo.
              const locFirst = (job.location || '').split('\n')[0].trim().toLowerCase();
              const nameIsAddress = !!(job.client_name && locFirst &&
                job.client_name.toLowerCase().includes(locFirst));
              // Calendar imports carry no client name — the sheet has it
              // but the client record never got it. Fall back to the site
              // contact before declaring "not on file".
              const displayName = (!nameIsAddress && job.client_name)
                || job.site_contact_name
                || null;
              const descIsAddress = !!(job.description && locFirst &&
                (job.description.toLowerCase().includes(locFirst) ||
                 job.description.toLowerCase().startsWith('soil test at')));
              return (
              <Paper
                key={job.id || idx}
                elevation={0}
                sx={{
                  p: 2,
                  mb: 1.5,
                  border: '1px solid #E2E8F0',
                  borderRadius: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {job.scheduled_time ? job.scheduled_time.slice(0, 5) : 'TBD'}
                  </Typography>
                  <Chip
                    label={job.status}
                    size="small"
                    color={
                      job.status === 'Completed' ? 'success' :
                      job.status === 'Failed' ? 'error' :
                      job.status === 'In Progress' ? 'warning' :
                      'default'
                    }
                    variant="outlined"
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOn fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {job.location || 'No location set'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Person fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {displayName || 'Client name not on file'}
                  </Typography>
                </Box>
                {(job.site_contact_phone || job.client_phone) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Phone fontSize="small" color="action" />
                    <Typography
                      variant="body2"
                      component="a"
                      href={`tel:${job.site_contact_phone || job.client_phone}`}
                      sx={{ color: 'primary.main', fontWeight: 600, textDecoration: 'none' }}
                    >
                      {job.site_contact_name ? `${job.site_contact_name} — ` : ''}
                      {job.site_contact_phone || job.client_phone}
                    </Typography>
                  </Box>
                )}
                {job.description && !descIsAddress && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    {job.description}
                  </Typography>
                )}
                {job.site_access && (
                  <Typography variant="caption" color="text.secondary">
                    Access: {job.site_access}
                  </Typography>
                )}
                {job.special_instructions && (
                  <Typography variant="caption" color="warning.main" fontWeight={600}>
                    {job.special_instructions}
                  </Typography>
                )}
                {/* Read-only preview — Navigate is harmless (opens Maps).
                    Status actions live on Home where outcomes are enforced. */}
                {date === todayStr && !['Completed', 'Failed', 'Cancelled'].includes(job.status) && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                    {job.location && (
                      <Button
                        variant="outlined" size="small" startIcon={<Navigation />}
                        onClick={() => window.open(
                          `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(job.location)}`,
                          '_blank')}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                      >
                        Navigate
                      </Button>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      Start / finish jobs from the Home tab
                    </Typography>
                  </Box>
                )}
              </Paper>
            );
            })}
          </Box>
        ))
      )}
    </Box>
  );
};

export default WorkerSchedule;
